import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Grid, Divider } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { fetchMembersByProgram, fetchWorkersByProgram, releaseWorker } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function BuildProgramMemberDetailPage() {
  const { businessDomainId, businessUnitId, programId, memberId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [data, setData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const memberListPath = `/build/${businessDomainId}/business-units/${businessUnitId}/programs/${programId}/members`;

  const loadData = useCallback(async () => {
    try {
      const [members, workers] = await Promise.all([
        fetchMembersByProgram(programId).catch(() => []),
        fetchWorkersByProgram(programId).catch(() => []),
      ]);
      const found = (Array.isArray(members) ? members : []).find(
        (m) => String(m.memberId) === String(memberId)
      );
      const foundWorker = (Array.isArray(workers) ? workers : []).find(
        (w) => String(w.memberId) === String(memberId)
      );
      if (found) {
        setData({ ...found, workerId: foundWorker?.workerId, workerName: foundWorker?.workerName });
      } else {
        setData(null);
      }
    } catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId, memberId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRelease = async () => {
    try {
      const res = await releaseWorker(data.workerId);
      if (res?.result !== "OK") {
        throw new Error(res?.errorMessage || "release_failed");
      }
      notify.success(t("message.release.success"));
      navigate(memberListPath, { replace: true });
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  if (!data) return null;

  const fullName = `${data.lastName || ""} ${data.firstName || ""}`.trim();
  const memberTypeLabel = data.industryId === "9999" ? t("member.type.company") : t("member.type.market");

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("program.member.detail.title")}</Typography>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12} md={6}>
            <DetailField label={t("member.name")} value={fullName} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField label={t("member.type")} value={memberTypeLabel} />
          </Grid>
          {data.email && (
            <Grid size={12} md={6}>
              <DetailField label={t("member.email")} value={data.email} />
            </Grid>
          )}
          {data.memberDescription && (
            <Grid size={12}>
              <DetailField label={t("member.description")} value={data.memberDescription} />
            </Grid>
          )}
          {data.memberClass && (
            <Grid size={12} md={6}>
              <DetailField label={t("member.class")} value={data.memberClass} />
            </Grid>
          )}
          {data.workerName && (
            <Grid size={12} md={6}>
              <DetailField label={t("project.member.worker.name")} value={data.workerName} />
            </Grid>
          )}
        </Grid>
      </DetailSection>

      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          startIcon={<ZoomInIcon />}
          onClick={() => navigate(`/build/${businessDomainId}/business-units/${businessUnitId}/programs/${programId}/goal-achievements`)}
        >
          {t("program.goalAchievement.list.title")}
        </Button>
        <Button variant="contained" color="error" onClick={() => setConfirmOpen(true)}>
          {t("member.release")}
        </Button>
        <Button variant="outlined" onClick={() => navigate(`/build/${businessDomainId}/business-units/${businessUnitId}/programs/${programId}/members`)}>{t("common.back")}</Button>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        onConfirm={handleRelease}
        onCancel={() => setConfirmOpen(false)}
      />
    </Box>
  );
}
